"use client";

/**
 * First-party plugin — @mml/export-multi.
 *
 * Phase 25-AB Task 15: wraps the Task 12 ExportModal as a plugin.
 *
 * Also registers four representative exporters (mp4 / capcut-draft / srt /
 * mml-bundle) drawn from the Task 12 format catalog so future code can
 * iterate registry.listExporters() instead of reading `formats.ts` directly.
 */

import { useState } from "react";
import { ExportModal } from "@/components/export/ExportModal";
import type { ExportInput, ExportResult, StudioPlugin } from "@/plugin-sdk";

function ExportSlot() {
	const [open, setOpen] = useState(true);
	return <ExportModal open={open} onClose={() => setOpen(false)} />;
}

async function noopExport(_input: ExportInput): Promise<ExportResult> {
	// First-party plugins delegate to the existing ExportModal backend.
	// The exporter entries below act as a discovery surface only.
	return { ok: true, files: [] };
}

export const exportMultiPlugin: StudioPlugin = {
	manifest: {
		id: "@mml/export-multi",
		version: "0.1.0",
		name: "Multi-format Export",
		description:
			"다중 포맷 익스포트 — 26 formats × 6 categories + CapCut 자동 임포트",
		author: "makemonlab",
	},
	activate(api) {
		api.registerPanel({
			id: "export-multi-modal",
			label: "Multi-format Export",
			slot: "export-modal",
			render: () => <ExportSlot />,
		});
		api.registerCommand({
			id: "export-multi.open",
			label: "익스포트 모달 열기",
			shortcut: "Ctrl+Shift+E",
			category: "Export",
			handler: () => {
				api.notify("Export modal triggered", "info");
			},
		});
		api.registerExporter({
			key: "mp4",
			label: "MP4 (H.264)",
			ext: "mp4",
			category: "video",
			description: "표준 H.264 MP4 — 1080p 12 Mbps 기본",
			exportFn: noopExport,
		});
		api.registerExporter({
			key: "capcut-draft",
			label: "CapCut Draft",
			ext: "zip",
			category: "nle",
			description: "CapCut 자동 임포트 가능한 draft 폴더 zip",
			exportFn: noopExport,
		});
		api.registerExporter({
			key: "srt",
			label: "SRT 자막",
			ext: "srt",
			category: "subtitle",
			description: "표준 SubRip 자막 파일",
			exportFn: noopExport,
		});
		api.registerExporter({
			key: "mml-bundle",
			label: "MML 통합 패키지",
			ext: "zip",
			category: "bundle",
			description: "영상 + 자막 + 메타데이터 + 라이선스 정보 묶음",
			exportFn: noopExport,
		});
	},
};
